/**
 * FAQSection
 *
 * Accordion-style FAQ for the landing page.
 * Only one item can be open at a time.
 */

import { useState } from 'react';

const FAQS = [
  {
    question: 'How does MomentSent know when to send my card?',
    answer:
      'For recurring occasions, birthdays, anniversaries, holidays, you set the date once and we automatically mail it every year, timed to arrive right on time. For one-time cards like a thinking-of-you or graduation, you pick the exact arrival date when you schedule it. Either way, we mail it about a week before your chosen date to account for delivery time.',
  },
  {
    question: 'Are these real physical cards, or digital eCards?',
    answer:
      'Real, physical greeting cards. We print your message on quality cardstock and mail it first-class so a tangible card arrives in your recipient\'s mailbox. MomentSent is not an eCard service or a digital "experience page", it\'s an actual card in the mail.',
  },
  {
    question: 'How does pricing work?',
    answer:
      'Each plan includes a yearly allowance of folded cards: 12 on Thoughtful, 24 on Connected, and 48 on Premium, with printing and first-class postage included. If you send more than your allowance, extra cards are a flat $5.99 each, only when you go past the included amount. Your plan price also sets how many people you can send to (up to 5, up to 20, or unlimited).',
  },
  {
    question: 'What does the card look like? Is it a generic template?',
    answer:
      'Not at all. You write the message in your own words, exactly what you want printed on the card. It\'s completely personal because it comes from you. You can preview how it looks before it goes to print.',
  },
  {
    question: 'Can I see the message before it\'s mailed?',
    answer:
      'Yes, always. For recurring cards, you\'ll get a notification 14 days before the send date with the draft message. You can approve it, edit it, or rewrite it completely. For one-time cards, you write your personal message right when you schedule it. Either way, we never mail anything you haven\'t had a chance to review.',
  },
  {
    question: 'What if someone moves or their address changes?',
    answer:
      'Just update the address in your contact\'s profile. We validate addresses through the USPS database before every send, and we\'ll flag anything that looks undeliverable so you can fix it in time.',
  },
  {
    question: 'Do I need a subscription to use MomentSent?',
    answer:
      'You can start with a 14-day free trial, no credit card required. After that, plans start at $7.99/month for up to 5 contacts. The Connected plan ($15.99/month) covers up to 20 contacts, and the Premium plan ($27.99/month) covers unlimited contacts. Each plan includes a yearly allowance of real folded cards (12, 24, or 48); any extra cards are a flat $5.99 each.',
  },
  {
    question: 'What countries do you ship to?',
    answer:
      'We mail to over 100 countries through our print and fulfillment partners. US addresses ship standard first-class. International delivery typically takes 7 to 14 business days depending on the destination.',
  },
  {
    question: 'Is MomentSent only for birthdays?',
    answer:
      'Not at all. Birthdays are just the start. We also send cards for anniversaries, Christmas, Valentine\'s Day, Mother\'s Day, Father\'s Day, graduations, thinking of you notes, and any custom occasion you name. Check the "Every occasion, covered" section above for the full list. If there\'s a date that matters, we remember it.',
  },
] as const;

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="bg-white py-24">
      <div className="section-container">

        {/* Heading */}
        <div className="text-center mb-14">
          <p className="font-body text-xs font-semibold tracking-widest text-sage uppercase mb-3">
            Frequently asked questions
          </p>
          <h2 className="section-heading text-4xl sm:text-5xl">
            Everything you need to know
          </h2>
        </div>

        {/* Accordion */}
        <div className="max-w-2xl mx-auto">
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={index} className="border-b border-gray-100">
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-heading text-base text-navy">
                    {faq.question}
                  </span>
                  <span
                    className="flex-shrink-0 text-sage text-xl font-light leading-none transition-transform duration-200"
                    aria-hidden="true"
                  >
                    {isOpen ? '−' : '+'}
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[640px] pb-5' : 'max-h-0'
                  }`}
                >
                  <p className="font-body text-sm text-gray-500 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="font-body text-sm text-gray-400 text-center mt-12">
          Still have questions? Email us at{' '}
          <a
            href="mailto:hello@momentsent.com"
            className="underline underline-offset-2 hover:text-gray-500 transition-colors"
          >
            hello@momentsent.com
          </a>
        </p>

      </div>
    </section>
  );
}
